import type { BloomWindow, FloraSeasonDefinition, SeasonalAppearance } from './FloraDefinition';
import { seasonBlend, wrapDay, DAYS_IN_YEAR } from '@/environment/SeasonModel';

/**
 * Per-plant seasonal appearance resolution (spec §5.3). Blends the four seasonal appearances by day
 * of year, applies deciduous leaf-out/autumn/leaf-drop logic, and evaluates bloom windows. Pure and
 * deterministic so date changes produce smooth, testable transitions.
 */

export interface SeasonalRenderState {
  foliageColor: string;
  foliageDensity: number;
  flowerColor: string | null;
  flowerDensity: number;
  fruitColor: string | null;
  fruitDensity: number;
  branchVisibility: number;
  dormant: boolean;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function parseHex(hex: string): Rgb {
  const clean = hex.replace('#', '');
  const value = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean.padEnd(6, '0').slice(0, 6);
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

function toHex(rgb: Rgb): string {
  const part = (n: number): string =>
    Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
  return `#${part(rgb.r)}${part(rgb.g)}${part(rgb.b)}`;
}

/** Linearly interpolate between two hex colors. */
export function lerpColor(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  return toHex({
    r: ca.r + (cb.r - ca.r) * t,
    g: ca.g + (cb.g - ca.g) * t,
    b: ca.b + (cb.b - ca.b) * t,
  });
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Blend two seasonal appearances. */
export function interpolateAppearance(
  a: SeasonalAppearance,
  b: SeasonalAppearance,
  t: number,
): SeasonalAppearance {
  const result: SeasonalAppearance = {
    foliageColor: lerpColor(a.foliageColor, b.foliageColor, t),
    foliageDensity: lerp(a.foliageDensity, b.foliageDensity, t),
    branchVisibility: lerp(a.branchVisibility, b.branchVisibility, t),
    dormant: t < 0.5 ? a.dormant : b.dormant,
  };
  const flowerColor = t < 0.5 ? a.flowerColor : b.flowerColor;
  if (flowerColor !== undefined) result.flowerColor = flowerColor;
  const flowerDensity = lerp(a.flowerDensity ?? 0, b.flowerDensity ?? 0, t);
  if (flowerDensity > 0) result.flowerDensity = flowerDensity;
  const fruitColor = t < 0.5 ? a.fruitColor : b.fruitColor;
  if (fruitColor !== undefined) result.fruitColor = fruitColor;
  const fruitDensity = lerp(a.fruitDensity ?? 0, b.fruitDensity ?? 0, t);
  if (fruitDensity > 0) result.fruitDensity = fruitDensity;
  return result;
}

const LEAF_TRANSITION_DAYS = 14;

/**
 * Fraction of foliage present on a given day for a deciduous plant (0 = bare, 1 = full). Ramps up
 * over {@link LEAF_TRANSITION_DAYS} after leaf-out and back down after leaf-drop. Evergreens are
 * always fully leafed.
 */
export function foliagePresence(def: FloraSeasonDefinition, dayOfYear: number): number {
  if (def.evergreen) return 1;
  const day = wrapDay(dayOfYear);
  const leafOut = def.leafOutDay ?? 100;
  const leafDrop = def.leafDropDay ?? 300;

  if (day < leafOut || day > leafDrop + LEAF_TRANSITION_DAYS) return 0;
  const outRamp = Math.min(1, (day - leafOut) / LEAF_TRANSITION_DAYS);
  const dropRamp = day <= leafDrop ? 1 : Math.max(0, 1 - (day - leafDrop) / LEAF_TRANSITION_DAYS);
  return Math.max(0, Math.min(outRamp, dropRamp));
}

/** Evaluate bloom intensity/color for a day, returning null when no window is active. */
export function bloomAt(
  windows: readonly BloomWindow[] | undefined,
  dayOfYear: number,
): { intensity: number; color: string } | null {
  if (!windows || windows.length === 0) return null;
  const day = wrapDay(dayOfYear);
  let best: { intensity: number; color: string } | null = null;
  for (const w of windows) {
    const start = w.startDayOfYear;
    let end = w.endDayOfYear;
    let d = day;
    if (end < start) {
      end += DAYS_IN_YEAR;
      if (d < start) d += DAYS_IN_YEAR;
    }
    if (d < start || d > end) continue;
    // Triangular ramp peaking at the window centre for a soft bloom.
    const span = end - start || 1;
    const phase = (d - start) / span;
    const ramp = 1 - Math.abs(phase - 0.5) * 2;
    const intensity = w.intensity * Math.max(0, ramp);
    if (best === null || intensity > best.intensity) best = { intensity, color: w.color };
  }
  return best;
}

/** Resolve the full seasonal render state for a plant on a given day (with per-plant offset). */
export function resolveSeasonalAppearance(
  def: FloraSeasonDefinition,
  dayOfYear: number,
  offsetDays = 0,
): SeasonalRenderState {
  const day = wrapDay(dayOfYear + offsetDays);
  const blend = seasonBlend(day);
  const base = interpolateAppearance(def[blend.from], def[blend.to], blend.t);

  const presence = foliagePresence(def, day);
  const foliageDensity = base.foliageDensity * presence;
  // Branches show through as foliage thins.
  const branchVisibility = lerp(1, base.branchVisibility, presence);

  // Autumn colour: shift foliage toward the autumn colour as the season progresses.
  let foliageColor = base.foliageColor;
  if (!def.evergreen && def.autumnColorStartDay !== undefined) {
    const start = def.autumnColorStartDay;
    const drop = def.leafDropDay ?? 300;
    if (day >= start && day <= drop && drop > start) {
      const progress = (day - start) / (drop - start);
      foliageColor = lerpColor(base.foliageColor, def.autumn.foliageColor, progress);
    }
  }

  const state: SeasonalRenderState = {
    foliageColor,
    foliageDensity,
    flowerColor: base.flowerColor ?? null,
    flowerDensity: base.flowerDensity ?? 0,
    fruitColor: base.fruitColor ?? null,
    fruitDensity: base.fruitDensity ?? 0,
    branchVisibility,
    dormant: presence < 0.05,
  };

  const bloom = bloomAt(def.bloomWindows, day);
  if (bloom !== null) {
    state.flowerColor = bloom.color;
    state.flowerDensity = Math.max(state.flowerDensity, bloom.intensity);
  }

  return state;
}
